import { IS_PRODUCTION } from '../../config/FruitfulEnvironment';

/** Google Tag Manager container id -- use with gtm.js for pushing events to GTM (and thus all of the other scripts/services it loads) */
export const GOOGLE_TAG_MANAGER_ID = 'GTM-M7DQSGB';

/** Google Analytics tag id -- use with gtag.js for pushing events directly to GA4 */
const GOOGLE_TAG_ID = 'G-PQCCYT6JS8';

type GoogleTagEventParams = Record<string, string | number | boolean>;

interface GoogleTagWindow {
    dataLayer?: unknown[];
    gtag?: (
        command: 'js' | 'config' | 'event',
        target: string | Date,
        params?: GoogleTagEventParams,
    ) => void;
}

export const isGoogleTagManagerLoaded = () =>
    document.querySelector(
        `script[src*="googletagmanager.com/gtm.js?id=${GOOGLE_TAG_MANAGER_ID}"]`,
    ) != null;

/** GTM custom-event push. The signup container reads this dataLayer shape. */
export const pushGoogleTagManagerEvent = (
    event: string,
    data?: GoogleTagEventParams,
) => {
    const googleTagWindow = window as Window & GoogleTagWindow;
    googleTagWindow.dataLayer = googleTagWindow.dataLayer ?? [];
    googleTagWindow.dataLayer.push({ event, ...data });
};

const gtagScriptSrc = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG_ID}`;

/**
 * Queue gtag commands before the script loads. Calls made after this returns
 * are processed when gtag.js arrives.
 */
const ensureGoogleTag = () => {
    const googleTagWindow = window as Window & GoogleTagWindow;
    googleTagWindow.dataLayer = googleTagWindow.dataLayer ?? [];
    const dataLayer = googleTagWindow.dataLayer;

    if (typeof googleTagWindow.gtag !== 'function') {
        googleTagWindow.gtag = function gtag() {
            // Google's snippet queues `arguments` so each command stays array-like.
            // eslint-disable-next-line prefer-rest-params
            dataLayer.push(arguments);
        };
    }

    if (document.querySelector(`script[src="${gtagScriptSrc}"]`) != null) {
        return;
    }

    googleTagWindow.gtag('js', new Date());
    googleTagWindow.gtag('config', GOOGLE_TAG_ID, { send_page_view: false });

    const script = document.createElement('script');
    script.async = true;
    script.src = gtagScriptSrc;
    document.head.appendChild(script);
};

/** Direct GA4 event for web production pages that are not running the GTM container. */
export const sendGoogleTagEvent = (
    event: string,
    data?: GoogleTagEventParams,
) => {
    if (!IS_PRODUCTION) {
        pushGoogleTagManagerEvent(event, data);
        return;
    }

    ensureGoogleTag();
    const googleTagWindow = window as Window & GoogleTagWindow;
    googleTagWindow.gtag?.('event', event, data);
};
